
/**
 * Simple audio feedback system for the voice assistant
 * Provides audible feedback about command processing states
 */

// Create and cache audio contexts for different sounds
const audioContexts: Record<string, AudioContext | null> = {};

// Sound type definitions 
type SoundType = 'success' | 'error' | 'info' | 'listening' | 'recognized';

/**
 * Play a simple beep sound with specified parameters
 * 
 * @param type - The type of sound to play
 * @returns Promise that resolves when the sound is complete
 */
export function playBeep(type: SoundType): Promise<void> {
  // Skip audio on browsers that don't support AudioContext
  if (typeof AudioContext === 'undefined') {
    return Promise.resolve();
  }

  // Configure different sounds based on type
  const config = getSoundConfig(type);
  
  try {
    // Create or reuse context
    if (!audioContexts[type]) {
      audioContexts[type] = new AudioContext();
    }
    
    const context = audioContexts[type];
    if (!context) return Promise.resolve();
    
    // Create oscillator
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    
    // Configure oscillator
    oscillator.type = config.waveform;
    oscillator.frequency.setValueAtTime(config.frequency, context.currentTime);
    if (config.frequencyEnd) {
      oscillator.frequency.linearRampToValueAtTime(
        config.frequencyEnd, 
        context.currentTime + config.duration
      );
    }
    
    // Configure gain (volume envelope)
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.linearRampToValueAtTime(config.volume, context.currentTime + 0.005);
    gain.gain.linearRampToValueAtTime(0, context.currentTime + config.duration);
    
    // Connect and start
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + config.duration);
    
    return new Promise(resolve => {
      setTimeout(resolve, config.duration * 1000);
    });
  } catch (err) {
    console.error('Audio feedback error:', err);
    return Promise.resolve();
  }
}

/**
 * Get sound configuration for different feedback types
 */
function getSoundConfig(type: SoundType) {
  switch (type) {
    case 'success':
      return {
        frequency: 1200,
        frequencyEnd: 1800,
        duration: 0.15,
        volume: 0.1,
        waveform: 'sine' as OscillatorType
      };
    case 'error':
      return {
        frequency: 400,
        frequencyEnd: 200,
        duration: 0.3,
        volume: 0.1,
        waveform: 'triangle' as OscillatorType
      };
    case 'info':
      return {
        frequency: 800,
        frequencyEnd: 1000,
        duration: 0.1,
        volume: 0.08,
        waveform: 'sine' as OscillatorType
      };
    case 'listening':
      return {
        frequency: 440,
        frequencyEnd: 880,
        duration: 0.2,
        volume: 0.1,
        waveform: 'sine' as OscillatorType
      };
    case 'recognized':
      return {
        frequency: 880,
        frequencyEnd: 440,
        duration: 0.2,
        volume: 0.1,
        waveform: 'sine' as OscillatorType
      };
    default:
      return {
        frequency: 800,
        duration: 0.1,
        volume: 0.1,
        waveform: 'sine' as OscillatorType
      };
  }
}

/**
 * Returns true if audio feedback is supported in this browser
 */
export function isAudioFeedbackSupported(): boolean {
  return typeof AudioContext !== 'undefined';
}
